
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Simple2DMPRViewport, { mprState } from './Simple2DMPRViewport';

const WL = {
  '1': { ww: 400,  wc: 40,   label: 'Soft tissue' },
  '2': { ww: 1500, wc: -600, label: 'Lung'        },
  '3': { ww: 150,  wc: 90,   label: 'Brain'       },
  '4': { ww: 2500, wc: 480,  label: 'Bone'        },
  '5': { ww: 80,   wc: 40,   label: 'Soft'        },
  '6': { ww: 40,   wc: 40,   label: 'Liver'       },
};

function MPRPanel({ imageIds, displaySetUID, onClose }) {
  // Init shared state from cache meta
  useEffect(() => {
    const meta = window.__mprSliceCache && window.__mprSliceCache[displaySetUID + '_meta'];
    if (meta) {
      Object.assign(mprState, {
        depth: meta.depth, rows: meta.rows, columns: meta.columns,
        ww: meta.ww, wc: meta.wc,
        axialIdx: meta.axialMid, corIdx: meta.corMid, sagIdx: meta.sagMid,
      });
    }
  }, [displaySetUID]);

  // Keyboard shortcuts
  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') { onClose(); return; }
      if (WL[e.key]) {
        window.dispatchEvent(new CustomEvent('mprApplyWL', { detail: WL[e.key] }));
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    // position:absolute fills the viewport container div exactly
    // The OHIF toolbar row above is NOT inside this container so stays visible
    <div style={{
      position:   'absolute',
      inset:       0,
      zIndex:      100,
      display:    'flex',
      flexDirection: 'column',
      background: '#111',
    }}>
      {/* Compact toolbar row */}
      <div style={{
        display:        'flex',
        alignItems:     'center',
        flexWrap:       'wrap',
        gap:             4,
        padding:        '3px 8px',
        background:     '#1c1c1c',
        borderBottom:   '1px solid #333',
        flexShrink:      0,
        minHeight:       30,
      }}>
        <span style={{ color:'#4fc3f7', fontSize:12, fontWeight:'bold', marginRight:6 }}>
          2D MPR
        </span>
        {Object.entries(WL).map(([k, p]) => (
          <button key={k}
            title={`Keyboard: ${k}`}
            onClick={() => window.dispatchEvent(new CustomEvent('mprApplyWL', { detail: p }))}
            style={{
              background:'#2a2a2a', border:'1px solid #444', color:'#ddd',
              padding:'2px 8px', borderRadius:3, cursor:'pointer', fontSize:11,
            }}>
            <b style={{ color:'#4fc3f7' }}>{k}</b> {p.label}
          </button>
        ))}
        <span style={{ color:'#444', fontSize:10, marginLeft:4 }}>
          AX: use toolbar above · COR/SAG: L=scroll R=WL ⟲=scroll
        </span>
        <div style={{ flex:1 }} />
        <button onClick={onClose} style={{
          background:'#c0392b', border:'none', color:'#fff',
          padding:'3px 12px', borderRadius:3,
          cursor:'pointer', fontSize:12, fontWeight:'bold',
        }}>
          ✕ Exit MPR
        </button>
      </div>

      {/* 3 equal viewport panels */}
      <div style={{
        display:'grid', gridTemplateColumns:'1fr 1fr 1fr',
        flex:1, gap:2, padding:2, minHeight:0, background:'#1a1a1a',
      }}>
        {['axial','coronal','sagittal'].map(o => (
          <div key={o} style={{ position:'relative', overflow:'hidden' }}>
            <Simple2DMPRViewport
              imageIds={imageIds}
              orientation={o}
              displaySetUID={displaySetUID}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Simple2DMPRLayout({ imageIds, displaySetUID, onClose }) {
  const [container, setContainer] = useState(null);

  useEffect(() => {
    // Target the active viewport container — the div that holds the current image
    // OHIF marks it with data-cy="viewport-container-0"
    // Fallback: .viewport-drop-target (first one)
    const el =
      document.querySelector('[data-cy="viewport-container-0"]') ||
      document.querySelector('.viewport-drop-target') ||
      document.querySelector('.main-content') ||
      document.body;
    setContainer(el);
  }, []);

  if (!container) return null;
  return ReactDOM.createPortal(
    <MPRPanel imageIds={imageIds} displaySetUID={displaySetUID} onClose={onClose} />,
    container
  );
}

Simple2DMPRLayout.propTypes = {
  imageIds:      PropTypes.arrayOf(PropTypes.string).isRequired,
  displaySetUID: PropTypes.string,
  onClose:       PropTypes.func.isRequired,
};
